import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import ChakraBodyDiagram from '../components/ChakraBodyDiagram';
import ChakraInfoPanel from '../components/ChakraInfoPanel';
import { useAuth } from '../contexts/AuthContext';

const chakraData = [
  {
    key: 'crown',
    name: 'Crown Chakra',
    color: '#8e24aa',
    location: 'Top of Head',
    emotion: 'Spirituality, Connection',
    imbalance: 'Disconnection, cynicism, apathy',
    practices: 'Meditation, silence, prayer, OM mantra',
    description: 'Represents pure consciousness and connection to the divine. Associated with wisdom, transcendence, and enlightenment.'
  },
  {
    key: 'thirdEye',
    name: 'Third Eye Chakra',
    color: '#3949ab',
    location: 'Forehead (between eyebrows)',
    emotion: 'Intuition, Insight',
    imbalance: 'Confusion, lack of focus, nightmares',
    practices: 'Visualization, dreamwork, chanting OM',
    description: 'Center of intuition, imagination, and inner wisdom. Governs perception and psychic abilities.'
  },
  {
    key: 'throat',
    name: 'Throat Chakra',
    color: '#1e88e5',
    location: 'Throat',
    emotion: 'Communication, Truth',
    imbalance: 'Shyness, dishonesty, sore throat',
    practices: 'Singing, chanting, journaling, blue gemstones',
    description: 'Seat of communication, self-expression, and truth. Supports honest, clear speech and creative expression.'
  },
  {
    key: 'heart',
    name: 'Heart Chakra',
    color: '#43a047',
    location: 'Center of Chest',
    emotion: 'Love, Compassion',
    imbalance: 'Loneliness, jealousy, bitterness',
    practices: 'Loving-kindness meditation, gratitude, green foods',
    description: 'Bridge between body and spirit. Governs love, compassion, and emotional balance.'
  },
  {
    key: 'solarPlexus',
    name: 'Solar Plexus Chakra',
    color: '#fbc02d',
    location: 'Upper Abdomen',
    emotion: 'Confidence, Willpower',
    imbalance: 'Low self-esteem, anger, digestive issues',
    practices: 'Core yoga, affirmations, sunlight',
    description: 'Center of personal power, confidence, and self-discipline. Fuels motivation and transformation.'
  },
  {
    key: 'sacral',
    name: 'Sacral Chakra',
    color: '#fb8c00',
    location: 'Lower Abdomen',
    emotion: 'Creativity, Pleasure',
    imbalance: 'Guilt, emotional instability, sexual issues',
    practices: 'Dance, creative arts, hip-opening yoga',
    description: 'Seat of creativity, pleasure, and emotional flow. Governs sexuality and relationships.'
  },
  {
    key: 'root',
    name: 'Root Chakra',
    color: '#d32f2f',
    location: 'Base of Spine',
    emotion: 'Stability, Security',
    imbalance: 'Fear, anxiety, financial stress',
    practices: 'Grounding, walking barefoot, red foods',
    description: 'Foundation of physical and emotional security. Supports survival, grounding, and trust.'
  }
];

function getChakraData() {
  return chakraData;
}

const ChakraEducationPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeChakra, setActiveChakra] = useState('heart');
  const [hoveredChakra, setHoveredChakra] = useState(null);
  const chakraList = getChakraData();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleChakraClick = (key) => setActiveChakra(key);
  const handleChakraHover = (key) => setHoveredChakra(key);
  const handleChakraLeave = () => setHoveredChakra(null);

  const selectedChakra = chakraList.find(c => c.key === (hoveredChakra || activeChakra));

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default, py: 0 }}>
      <NavBar user={user} />
      <Box sx={{ maxWidth: 1200, mx: 'auto', py: { xs: 3, md: 6 }, px: 2 }}>
        <Link onClick={() => navigate('/mantras')} sx={{ cursor: 'pointer', fontWeight: 600, color: 'primary.main', mb: 3, display: 'inline-block', fontSize: '1.1rem' }}>
          ← Back to Mantras
        </Link>
        <Typography variant="h4" fontWeight={700} sx={{ fontFamily: 'Playfair Display, serif', color: 'primary.main', mb: 4 }}>
          Chakra Education
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6 }}>
          {/* Left: Body Diagram */}
          <Box sx={{ flex: isMobile ? 'unset' : 3, display: 'flex', justifyContent: 'center', alignItems: 'center', mb: isMobile ? 4 : 0 }}>
            <ChakraBodyDiagram
              onSelectChakra={handleChakraClick}
              activeChakraId={hoveredChakra || activeChakra}
              onHoverChakra={handleChakraHover}
              onLeaveChakra={handleChakraLeave}
            />
          </Box>
          {/* Right: Info Panel */}
          <Box sx={{ flex: isMobile ? 'unset' : 2, minWidth: 0 }}>
            <ChakraInfoPanel chakra={selectedChakra} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChakraEducationPage; 